import React, { useEffect, useState, useCallback, memo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../../config';
import { getToken } from '../../utils/auth';

const CartAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');
    const [data, setData] = useState({ carts: [], totals: {} });
    const [loading, setLoading] = useState(false);
    const headers = { Authorization: `Bearer ${getToken()}` };

    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/cart/admin/insights`, { headers });
            setData(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []); // eslint-disable-line

    useEffect(() => { fetchData(); }, [fetchData]);

    const carts = data.carts || [];
    const totals = data.totals || {};

    return (
        <div className="max-w-5xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">Cart Insights</h1>
                    <p className="text-gray-400 text-sm font-medium mt-1">Active shopping carts and estimated value</p>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={fetchData} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-all">
                        <i className="fas fa-sync-alt text-gray-500 text-xs" />
                    </button>
                    <button
                        onClick={() => navigate("/admin")}
                        className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
                    >
                        <span className={`transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 mb-10">
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-400 mb-1">Active Carts</p>
                    <p className="text-3xl font-black text-blue-700">{totals.active_carts || 0}</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-1">Total Items in Carts</p>
                    <p className="text-3xl font-black text-indigo-700">{totals.total_items || 0}</p>
                </div>
            </div>

            {loading ? (
                <div className="p-16 text-center text-gray-300 animate-pulse font-bold">Loading...</div>
            ) : carts.length === 0 ? (
                <div className="p-16 text-center">
                    <i className="fas fa-shopping-cart text-5xl text-gray-100 mb-4 block" />
                    <p className="text-gray-300 font-bold">No active carts</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-5 gap-4 px-4 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                        <span className="col-span-2">Customer</span>
                        <span className="text-center">Items</span>
                        <span className="text-center">Est. Value (USD)</span>
                        <span className="text-center">Last Active</span>
                    </div>
                    {carts.map(cart => (
                        <div key={cart.user_id} className="grid grid-cols-5 gap-4 items-center p-4 rounded-2xl border border-gray-100 hover:border-brand-primary/20 hover:bg-brand-primary/5 transition-all">
                            <div className="col-span-2 flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary font-black text-sm">
                                    {(cart.username || '?')[0].toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-black text-gray-900 truncate">{cart.username}</p>
                                    <p className="text-[10px] text-gray-400 font-bold truncate">{cart.email}</p>
                                </div>
                            </div>
                            <div className="text-center">
                                <span className="inline-flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-black text-gray-700">
                                    <i className="fas fa-box text-[9px]" /> {cart.total_qty || cart.item_count}
                                </span>
                            </div>
                            <div className="text-center">
                                <span className="text-sm font-black text-gray-900">
                                    ${parseFloat(cart.estimated_value_usd || 0).toFixed(2)}
                                </span>
                            </div>
                            <div className="text-center">
                                <span className="text-[10px] font-bold text-gray-400">
                                    {cart.last_updated ? new Date(cart.last_updated).toLocaleDateString() : '—'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default CartAdmin;
